import { useState, useEffect } from 'react';
import { api, getCategories } from '../api';
import { styles } from '../styles';
import { BackIcon } from '../components/Icons';

function formatDate(dateStr) {
  if (!dateStr) return 'Unknown date';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function DuplicateBadge({ status }) {
  if (status === 'new') return null;
  const isLikely = status === 'likely_duplicate';
  return (
    <span style={{
      ...reviewStyles.badge,
      backgroundColor: isLikely ? '#fee2e2' : '#fff7ed',
      color: isLikely ? '#dc2626' : '#ea580c',
    }}>
      {isLikely ? '⚠ Likely duplicate' : '? Possible duplicate'}
    </span>
  );
}

function MatchedReceiptHint({ row }) {
  if (row.duplicate_status === 'new' || !row.matched_amount) return null;
  const date = row.matched_date
    ? new Date(row.matched_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;
  return (
    <div style={reviewStyles.matchHint}>
      <span style={reviewStyles.matchLabel}>Existing receipt:</span>
      <span style={reviewStyles.matchValue}>
        {row.matched_merchant_name || 'Unknown'} · ${Number(row.matched_amount).toFixed(2)}
        {date ? ` · ${date}` : ''}
      </span>
    </div>
  );
}

export default function StatementReviewScreen({ transactions, currentUser, onDone, onBack }) {
  const [categories, setCategories] = useState([]);
  const [rows, setRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    if (!transactions) return;
    setRows(
      transactions.map((tx) => ({
        ...tx,
        accepted: tx.duplicate_status === 'new',
        categoryId: tx.suggested_category_id || '',
        expenseType: 'personal',
      }))
    );
  }, [transactions]);

  const selectedCount = rows.filter((r) => r.accepted).length;

  const updateRow = (idx, patch) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const handleImport = async () => {
    const items = rows
      .filter((r) => r.accepted && r.categoryId)
      .map((r) => ({
        merchant_name: r.merchant_name,
        amount: r.amount,
        transaction_date: r.transaction_date,
        category_id: r.categoryId,
        expense_type: r.expenseType,
      }));

    if (items.length === 0) return;

    setImporting(true);
    setError(null);
    try {
      await api.post(`/receipts/statement/import?user_id=${currentUser}`, { items });
      onDone();
    } catch (e) {
      setError('Import failed. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  if (!rows.length) {
    return (
      <div style={{ ...styles.screen, ...styles.centered }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: 18, fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>
            No transactions found
          </p>
          <p style={{ fontSize: 14, color: '#64748b', marginBottom: 24 }}>
            We couldn't extract any transactions from that image.
          </p>
          <button style={reviewStyles.backLink} onClick={onBack}>
            Try another image
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.screen, paddingBottom: 100 }}>
      <header style={styles.header}>
        <button style={styles.backBtn} onClick={onBack}>
          <BackIcon />
        </button>
        <h1 style={styles.headerTitle}>Review Transactions</h1>
        <div style={{ width: 20 }} />
      </header>

      <p style={reviewStyles.subtitle}>
        {rows.length} transaction{rows.length !== 1 ? 's' : ''} found.
        Duplicates are unchecked by default.
      </p>

      <div style={reviewStyles.list}>
        {rows.map((row, idx) => (
          <div
            key={idx}
            style={{
              ...reviewStyles.card,
              opacity: row.accepted ? 1 : 0.55,
            }}
          >
            {/* Row header: checkbox + merchant + amount */}
            <div style={reviewStyles.cardTop}>
              <input
                type="checkbox"
                checked={row.accepted}
                onChange={(e) => updateRow(idx, { accepted: e.target.checked })}
                style={reviewStyles.checkbox}
              />
              <div style={reviewStyles.merchantBlock}>
                <span style={reviewStyles.merchant}>
                  {row.merchant_name || 'Unknown merchant'}
                </span>
                <span style={reviewStyles.date}>{formatDate(row.transaction_date)}</span>
                <DuplicateBadge status={row.duplicate_status} />
              </div>
              <span style={reviewStyles.amount}>
                ${Number(row.amount).toFixed(2)}
              </span>
            </div>

            {/* Proof of matched existing receipt */}
            {!row.accepted && <MatchedReceiptHint row={row} />}

            {/* Category + type selectors (only shown when accepted) */}
            {row.accepted && (
              <div style={reviewStyles.cardFields}>
                <select
                  value={row.categoryId}
                  onChange={(e) => updateRow(idx, { categoryId: e.target.value })}
                  style={reviewStyles.select}
                >
                  <option value="">Select category…</option>
                  {categories.filter((c) => c.is_active).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.icon} {c.name}
                    </option>
                  ))}
                </select>

                <div style={reviewStyles.typeToggle}>
                  {['personal', 'corporate'].map((t) => (
                    <button
                      key={t}
                      style={{
                        ...reviewStyles.typeBtn,
                        ...(row.expenseType === t ? reviewStyles.typeBtnActive : {}),
                      }}
                      onClick={() => updateRow(idx, { expenseType: t })}
                    >
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {error && <p style={styles.error}>{error}</p>}

      {/* Sticky footer */}
      <div style={reviewStyles.footer}>
        <button
          style={{
            ...reviewStyles.importBtn,
            opacity: selectedCount === 0 || importing ? 0.5 : 1,
            cursor: selectedCount === 0 || importing ? 'default' : 'pointer',
          }}
          onClick={handleImport}
          disabled={selectedCount === 0 || importing}
        >
          {importing
            ? 'Importing…'
            : `Import ${selectedCount} transaction${selectedCount !== 1 ? 's' : ''}`}
        </button>
      </div>
    </div>
  );
}

const reviewStyles = {
  subtitle: {
    fontSize: 13,
    color: '#64748b',
    marginBottom: 16,
    marginTop: 0,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 12,
    border: '1px solid #e2e8f0',
    padding: '12px 14px',
    transition: 'opacity 0.15s',
  },
  cardTop: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
  },
  checkbox: {
    width: 18,
    height: 18,
    marginTop: 2,
    accentColor: '#6366f1',
    cursor: 'pointer',
    flexShrink: 0,
  },
  merchantBlock: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  merchant: {
    fontSize: 15,
    fontWeight: 600,
    color: '#1e293b',
  },
  date: {
    fontSize: 12,
    color: '#94a3b8',
  },
  badge: {
    fontSize: 11,
    fontWeight: 600,
    padding: '2px 7px',
    borderRadius: 6,
    alignSelf: 'flex-start',
    marginTop: 2,
  },
  amount: {
    fontSize: 15,
    fontWeight: 700,
    color: '#1e293b',
    flexShrink: 0,
  },
  cardFields: {
    marginTop: 10,
    paddingTop: 10,
    borderTop: '1px solid #f1f5f9',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  select: {
    width: '100%',
    padding: '8px 10px',
    fontSize: 14,
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    color: '#1e293b',
    outline: 'none',
  },
  typeToggle: {
    display: 'flex',
    gap: 6,
  },
  typeBtn: {
    flex: 1,
    padding: '6px 0',
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    backgroundColor: 'white',
    color: '#64748b',
    cursor: 'pointer',
  },
  typeBtnActive: {
    backgroundColor: '#6366f1',
    borderColor: '#6366f1',
    color: 'white',
  },
  footer: {
    position: 'fixed',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '100%',
    maxWidth: 480,
    padding: '12px 20px',
    backgroundColor: 'white',
    borderTop: '1px solid #e2e8f0',
    boxSizing: 'border-box',
  },
  importBtn: {
    width: '100%',
    padding: '14px 0',
    backgroundColor: '#6366f1',
    color: 'white',
    fontSize: 16,
    fontWeight: 600,
    borderRadius: 12,
    border: 'none',
    transition: 'opacity 0.15s',
  },
  backLink: {
    fontSize: 15,
    color: '#6366f1',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 500,
  },
  matchHint: {
    marginTop: 8,
    padding: '6px 10px',
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    display: 'flex',
    gap: 6,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  matchLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  matchValue: {
    fontSize: 12,
    color: '#475569',
  },
};
